import React, { Fragment } from 'react';
import FormTooltip from '../../common/FormTooltip';
import { ClearCircleIcon } from '../../../CircleIcon';
import Message from '../../common/Message';
import {
  RadioFieldWrapper,
  RadioField,
  StyledLabel,
  StyledInput,
  SelectorLabelWrapper,
  SelectorLabel,
  ToolTipWrapper,
  Dek,
  SelectorLabelRight,
  StyledExpandedArea,
  MessageWrapper,
} from './RadioFieldV2.styles';
import { IconWrapper } from '../../form.styles';

export type RadioFieldV2Props = {
  /** mandatory id for label matching */
  id: string;
  /** make a checkbox inline-block */
  inline?: boolean;
  /** label for the checkbox button */
  label?: string | React.ReactNode;
  /** custom function that triggered by onChange event of the input */
  onChange?: React.FormEventHandler<HTMLInputElement>;
  /** custom class name */
  className?: string;
  /** disable the checkbox button */
  disabled?: boolean;
  /** shows tooltip on hover of info icon */
  tooltip?: string;
  /** pass classname to adjust tooltip width according to text */
  tooltipClassname?: string;
  /** The text of any error message */
  errorMessage?: string;
  /** Whether or not the checkbox should show its children. Only used for checkboxes which should be expandable. */
  isExpanded?: boolean;
  /** The contents that are shown when a checkbox is expanded */
  children?: React.ReactElement;
  /** Whether or not the checkbox is checked / === true  */
  checked?: boolean;
  /** Whether or not the checkbox uses the selector style, i.e. the big, bordered version */
  useSelectorStyle?: boolean;
  /** The text that appears below the label */
  dek?: string | React.ReactNode;
  /** The path to the zola icon that can appear to the right of the label */
  rightSideZolaIconPath?: string;
  /** Text that can appear to the right of the label, bolded */
  rightSideText?: string;
  /** Typically an <img> or <svg> tag. Should be 36px tall */
  rightSideHtml?: React.ReactNode;
};

const RadioFieldV2: React.FC<RadioFieldV2Props> = ({
  id,
  label,
  className,
  inline,
  disabled,
  tooltip,
  errorMessage,
  isExpanded,
  children,
  checked,
  useSelectorStyle,
  dek,
  rightSideZolaIconPath,
  rightSideText,
  rightSideHtml,
  tooltipClassname,
  ...rest
}) => {
  const shownIcon = rightSideZolaIconPath ? (
    <IconWrapper>
      <ClearCircleIcon image={rightSideZolaIconPath} size={36} />
    </IconWrapper>
  ) : null;

  const tooltipProp = tooltip ? { 'data-tooltip': tooltip } : {};
  return (
    <Fragment>
      <RadioFieldWrapper
        error={!!errorMessage}
        checked={checked}
        disabled={disabled}
        inline={inline}
        selectorStyle={useSelectorStyle}
        className={className}
        {...tooltipProp}
      >
        <RadioField disabled={disabled} selectorStyle={useSelectorStyle}>
          <StyledInput {...rest} type="radio" id={id} disabled={disabled} checked={checked} />
          <StyledLabel
            htmlFor={id}
            checked={checked}
            disabled={disabled}
            error={!!errorMessage}
            selectorStyle={useSelectorStyle}
          >
            <SelectorLabelWrapper>
              <SelectorLabel disabled={disabled} checked={checked}>
                {label && label}
                {tooltip && (
                  <ToolTipWrapper>
                    <FormTooltip text={tooltip} className={tooltipClassname} />
                  </ToolTipWrapper>
                )}
                {dek && (
                  <Dek disabled={disabled} selectorStyle={useSelectorStyle}>
                    {dek}
                  </Dek>
                )}
              </SelectorLabel>
              {(rightSideText || rightSideZolaIconPath || rightSideHtml) && (
                <SelectorLabelRight disabled={disabled}>
                  {rightSideText && rightSideText}
                  {shownIcon && shownIcon}
                  {rightSideHtml && rightSideHtml}
                </SelectorLabelRight>
              )}
            </SelectorLabelWrapper>
          </StyledLabel>
          {errorMessage && !useSelectorStyle && (
            <MessageWrapper>
              <Message type="error" text={errorMessage} />
            </MessageWrapper>
          )}
        </RadioField>
        {isExpanded && children && (
          <StyledExpandedArea selectorStyle={useSelectorStyle}>{children}</StyledExpandedArea>
        )}
      </RadioFieldWrapper>
      {errorMessage && useSelectorStyle && <Message type="error" text={errorMessage} />}
    </Fragment>
  );
};

export default RadioFieldV2;
